/**
 * The caller's own event registrations, joined by eventId (FEA-16, docs/
 * FRONTEND-PLAN.md §2.5). This is the only way the UI can know registration
 * state — never invent it client-side (that was the old Events.jsx's
 * session-local Set, now superseded).
 *
 * `GET /events/registrations/mine` is paginated but a member's own
 * registration count is tiny, so this hook follows `lastEvaluatedKey` to
 * exhaustion (bounded, like the calendar's own /events fetch in
 * Schedules.jsx) and exposes a flat map instead of a page.
 *
 * Cancelled registrations are dropped from the map: for every UI purpose
 * here a cancelled registration is the same as never having registered —
 * the member can register again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as eventsApi from '../api/events';

const MAX_PAGES = 20;
const PAGE_LIMIT = 100;

async function fetchAllMine() {
  const all = [];
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // Sequential by nature: each request needs the previous page's cursor.
    // eslint-disable-next-line no-await-in-loop
    const data = await eventsApi.listMyRegistrations({
      limit: PAGE_LIMIT,
      lastEvaluatedKey: cursor
    });
    (data.registrations || []).forEach((r) => all.push(r));
    cursor = data.lastEvaluatedKey || null;
    if (!cursor) break;
  }
  return all;
}

export function useMyRegistrations() {
  const [state, setState] = useState({ loading: true, error: null, byEventId: {} });
  const runIdRef = useRef(0);

  const load = useCallback(() => {
    const runId = (runIdRef.current += 1);
    setState((current) => ({ ...current, loading: true, error: null }));
    fetchAllMine()
      .then((registrations) => {
        if (runId !== runIdRef.current) return;
        const byEventId = {};
        registrations.forEach((r) => {
          if (r.status !== 'cancelled') byEventId[r.eventId] = r;
        });
        setState({ loading: false, error: null, byEventId });
      })
      .catch((err) => {
        if (runId !== runIdRef.current) return;
        setState((current) => ({ ...current, loading: false, error: err }));
      });
  }, []);

  useEffect(() => {
    load();
    return () => {
      runIdRef.current += 1;
    };
  }, [load]);

  /** Patch one event's registration locally after register/cancel, no refetch. */
  const setRegistration = useCallback((eventId, registration) => {
    setState((current) => {
      const byEventId = { ...current.byEventId };
      if (registration && registration.status !== 'cancelled') {
        byEventId[eventId] = registration;
      } else {
        delete byEventId[eventId];
      }
      return { ...current, byEventId };
    });
  }, []);

  return { ...state, reload: load, setRegistration };
}

export default useMyRegistrations;
